import { FunctionComponent, useCallback } from 'react'
import {
  ButtonProps,
  CheckboxProps,
  ChipProps,
  RadioProps,
  isCheckboxProps,
  isRadioProps,
} from './types'
import useStyles from './styles'
import { useStateStyles, useValidateState } from './utils'

export const Chip: FunctionComponent<
  (ButtonProps | CheckboxProps | RadioProps) & ChipProps
> = props => {
  const {
    styles: overrides,
    variant: propVariant,
    checked,
    disabled,
    children,
    startIcon,
    endIcon,
    renderAs,
    ...rest
  } = props
  const hasIcon = !!(startIcon || endIcon)
  const { variant } = useValidateState({ propVariant })
  const styles = useStyles({ hasIcon, variant })
  const { activeStyles, disabledStyles } = useStateStyles({
    checked,
    disabled,
    styles,
    overrides,
  })

  const RootElement = useCallback(
    (props: (ButtonProps | RadioProps | CheckboxProps) & ChipProps) => {
      if (isRadioProps(props)) {
        const { renderAs, ...rest } = props
        return <input type="radio" {...rest} />
      }

      if (isCheckboxProps(props)) {
        const { renderAs, ...rest } = props
        return <input type="checkbox" {...rest} />
      }

      const { renderAs, ...rest } = props as ButtonProps & ChipProps
      return <button {...rest} />
    },
    [],
  )
  return (
    <div
      className="hero-chip_root"
      data-testid="hero-chip"
      css={[styles.root, overrides?.root]}
    >
      {renderAs === 'button' ? (
        <RootElement
          className="hero-chip_button"
          css={[
            styles.chip,
            overrides?.chip,
            ...activeStyles,
            ...disabledStyles,
          ]}
          disabled={disabled}
          {...rest}
        >
          {startIcon && (
            <span
              css={[styles.iconBox, overrides?.iconBox]}
              onClick={event => {
                event.stopPropagation()
              }}
              data-testid="startIcon"
            >
              {startIcon}
            </span>
          )}

          <span
            className="hero-chip_label"
            css={[styles.label, overrides?.label]}
          >
            {children}
          </span>

          {endIcon && (
            <span
              css={[styles.iconBox, overrides?.iconBox]}
              onClick={event => {
                event.stopPropagation()
              }}
              data-testid="endIcon"
            >
              {endIcon}
            </span>
          )}
        </RootElement>
      ) : (
        <div
          css={[
            styles.chip,
            overrides?.chip,
            ...activeStyles,
            ...disabledStyles,
          ]}
        >
          {startIcon && (
            <span
              css={[styles.iconBox, overrides?.iconBox]}
              onClick={event => {
                event.stopPropagation()
              }}
              data-testid="startIcon"
            >
              {startIcon}
            </span>
          )}
          <label
            className="hero-chip_label"
            css={[styles.label, overrides?.label]}
          >
            {children}
          </label>
          <RootElement
            className="hero-chip_input"
            css={[styles.input, overrides?.input]}
            disabled={disabled}
            checked={checked}
            renderAs={renderAs}
            {...rest}
          />
          {endIcon && (
            <span
              css={[styles.iconBox, overrides?.iconBox]}
              onClick={event => {
                event.stopPropagation()
              }}
              data-testid="endIcon"
            >
              {endIcon}
            </span>
          )}
        </div>
      )}
    </div>
  )
}
